import type { Category } from '@models/data';
import { HIDE_UNIT_SWITCH_CATEGORIES, INCH_COMPATIBLE_CATEGORIES, PRIMARY_CATEGORY } from './constants';
import { abby, getFeatureFlagValue } from '@libs/abby/abby';
import { isCategoryEnabled } from '@icommerce/categories';
import type { FeatureFlag } from '@icommerce/feature-flags';

export const CATEGORY_BARSTOCK = 'bar_stocks';
export const CATEGORY_E_CHAIN = 'e_chains';
export const CATEGORY_DRYLIN_LINEAR_TECHNOLOGY = 'drylin_linear_technology';
export const CATEGORY_DRYLIN_T_RAIL_GUIDE = 't_rail_guide';
export const CATEGORY_BEARINGS_IBH = [
  'sleeve_bearing',
  'sleeve_bearing_flange',
  'clearance_free_preloaded_bearings',
  'clearance_free_prestressed_bearing_with_flange',
  'thrust_washers',
  'disc_springs',
  'piston_rings',
  'clip_bearings',
  'split_bearings',
  'split_bearings_with_anti_rotation_feature',
  'easy_clip',
  'clip_in_double_flanged_bearings',
  'double_flanged_bearings',
  'flange_bearings',
  'pre_loaded_two_hole_flanged_bearings',
];

export class CategoryHelper {
  static extractCategoryIdsRecursive(category: Pick<Category, 'key' | 'children'>): string[] {
    if (!category.children) {
      return [category.key];
    }
    const children = category.children?.flatMap(child => this.extractCategoryIdsRecursive(child));
    return [category.key, ...children];
  }

  static isCategoryActive(category: Category, activeKey: string): boolean {
    return !!(
      category.key === activeKey || // is active category
      category.children?.find(subCategory =>
        // has active children
        this.isCategoryActive(subCategory, activeKey)
      )
    );
  }

  static isEnabled(keys: Array<string | undefined | null> = [], defaultValue = false) {
    const featureFlags = abby.getFeatureFlags();
    return isCategoryEnabled(
      keys,
      new Map<FeatureFlag, boolean>(featureFlags.map(f => [f.name, f.value])),
      defaultValue
    );
  }

  static isEChainAndForSelectedModelsOnly(categoryKeys: Array<string | undefined | null>): boolean {
    const isEChainCategory = categoryKeys.find(key => CategoryHelper.isEchain(key || '') || key === CATEGORY_E_CHAIN);

    if (!isEChainCategory) {
      return false;
    }
    return getFeatureFlagValue('showEChainSelectedModelsOnly');
  }

  /**
   * returns true if category belongs to chainflex
   */
  static isChainflex(categoryKey: string): boolean {
    return [
      'bus_cable',
      'fibre_optic_cable',
      'data_cable',
      'coax_cables',
      'measuring_system_cables',
      'servo_cables',
      'hybrid_cables',
      'motor_cables',
      'robot_cables',
      'special_cables',
      'clean_room_cable',
      'control_cables',
    ].includes(categoryKey);
  }

  /**
   * returns true if category belongs to readycable
   */
  static isReadycable(categoryKey: string): boolean {
    return [
      'Network_Ethernet_FOC_fieldbus_cables',
      'network_cables_ethernet',
      'network_cables_profibus',
      'network_cables_profinet',
      'network_cables_devicenet',
      'FOC',
      'video_vision_bus_systems',
      'video_vision_bus_technology_firewire',
      'video_vision_bus_technology_USB_2_0',
      'video_vision_bus_technology_USB_3_0',
      'video_vision_bus_technology_gige',
      'video_vision_bus_technology_DVI_D_HDMI',
      'video_vision_bus_technology_koax',
      'video_vision_bus_technology_vga',
      'sensor_and_actuator_cables',
      'RC_sensor_actuator_cables_CF9_CF_INI',
      'RC_sensor_actuator_cables_CF10_CF_INI',
      'RC_sensor_actuator_cables_CF98_CF_INI',
      'RC_sensor_actuator_cables_connection_cable',
      'cables_with_industrial_connectors',
      'industry_connector_cables_han_6b',
      'industry_connector_cables_han_10b',
      'industry_connector_cables_han_16b',
      'industry_connector_cables_han_24b',
      'FANUC',
      'ABB',
      'AIDA_standard',
      'KUKA',
      'suitable_for_Allen_Bradley',
      'suitable_for_B_R',
      'suitable_for_Baumueller',
      'suitable_for_Beckhoff',
      'suitable_for_Bosch_Rexroth',
      'suitable_for_Control_Techniques',
      'suitable_for_Siemens',
      'suitable_for_Danaher_Motion',
      'suitable_for_ELAU',
      'suitable_for_FAGOR',
      'suitable_for_FANUC',
      'suitable_for_Festo',
      'suitable_for_Heidenhain',
      'suitable_for_Jetter',
      'suitable_for_Berger_Lahr',
      'suitable_for_Lenze',
      'suitable_for_LinMot',
      'suitable_for_LTi_DRiVES',
      'suitable_for_Mitsubishi',
      'suitable_for_NUM',
      'suitable_for_Omron',
      'suitable_for_Parker',
      'suitable_for_SEW',
      'suitable_for_Stoeber',
      'suitable_for_Delta',
      'suitable_for_Harmonic_Drive',
      'suitable_for_Hengstler',
      'suitable_for_KEB',
      'suitable_for_Panasonic',
      'suitable_for_Wittenstein',
      'suitable_for_Bonfiglioli',
      'suitable_for_Danfoss',
      'suitable_for_KEBA',
      'suitable_for_Moog',
      'suitable_for_Heidrive',
      'suitable_for_Koco_Motion',
      'suitable_for_Motor_Power_Company',
      'suitable_for_schunk',
      'suitable_for_Baumer_Thalheim',
      'suitable_for_Sigmatek',
      'suitable_for_Keyence',
    ].includes(categoryKey);
  }

  static isBusCable(categoryKey: string) {
    return ['bus_cable'].includes(categoryKey);
  }

  static isMotorGearBox(categoryKey: string) {
    return ['motor_gearboxes'].includes(categoryKey);
  }

  static isToothedSlewingRings(categoryKey: string): boolean {
    return [PRIMARY_CATEGORY.TOOTHED_SLEWING_RINGS].includes(categoryKey);
  }

  static isSphericalBalls(categoryKey: string): boolean {
    return [PRIMARY_CATEGORY.SPHERICAL_BALLS].includes(categoryKey);
  }

  static isEmotor(categoryKey: string): boolean {
    return [
      'shaft_stepper_motors',
      'shaft_ec_bldc_motors',
      'spur_gear',
      'dc_notch_motors',
      'dc_protect_spur_gear',
      'linear_actuators',
      'motor_gearboxes',
      'motor_control_systems',
      'st_steppercable_motor_cables',
      'st_steppercable_feedback_cables',
      'st_steppercable_brake_cables',
      'ecbldc_cables_motor_cables',
      'ecbldc_cables_feedback_cables',
      'ecbldc_cables_brake_cables',
      'dc_cables_motor_cables',
      'dc_cables_feedback_cables',
      'connecting_cables_initiator_cables',
      'power_supplies',
      'coupling',
      'motor_flanges',
      'initiators',
      'external_brake_resistors',
      'mounting_accessories',
      'connector_set',
      'drylin_spacers',
      'operating_parts',
    ].includes(categoryKey);
  }

  // return true if category belongs to gears
  static isGear(categoryKey: string): boolean {
    return [
      'gears_gear_racks',
      'spur_gears',
      'bevel_gears',
      'bevel_gears_1_1',
      'bevel_gears_1_1_5',
      'bevel_gears_1_2',
      'bevel_gears_1_3',
      'bevel_gears_1_4',
      'bevel_gears_1_5',
      'gear_racks',
    ].includes(categoryKey);
  }

  /**
   * Checks if the provided category key is part of the predefined list of echain categories.
   *
   * @param {string} categoryKey - The category key to verify.
   * @return {boolean} Returns true if the category key is one of the valid echain categories; otherwise, false.
   * @deprecated This function looks as if it cannot reliably do what it promises in the name. There is an alternative,
   * see isEchainCategoryTree.
   */
  static isEchain(categoryKey: string): boolean {
    return ['linear_movement', '3D_movement', 'rotary_movement'].includes(categoryKey);
  }

  /**
   * Determines if the provided category tree contains the e-Chain category.
   * Assumes that the e-Chain category is a top level category in the PIM system.
   *
   * @param {Category} categoryTree - The category tree to be evaluated.
   * @return {boolean} Returns true if the category tree is an E-chain category tree, otherwise false.
   */
  static isEchainCategoryTree(categoryTree: Category): boolean {
    if (!categoryTree) {
      return false;
    }
    return CategoryHelper.extractCategoryIdsRecursive(categoryTree).includes(CATEGORY_E_CHAIN);
  }

  // return true if category belongs to ball bearings
  static isBallBearings(categoryKey: string): boolean {
    return ['ball_bearings'].includes(categoryKey);
  }

  static isRadialBallBearings(categoryKey: string): boolean {
    return ['radial_ball_bearings'].includes(categoryKey);
  }

  static isFlangeBallBearings(categoryKey: string): boolean {
    return ['flange_ball_bearings'].includes(categoryKey);
  }

  static isBallTransferUnit(categoryKey: string): boolean {
    return ['ball_transfer_unit'].includes(categoryKey);
  }

  static isBallConveyorRollers(categoryKey: string): boolean {
    return ['conveyor_rollers'].includes(categoryKey);
  }

  static isIgubalDoubleJoints(categoryKey: string): boolean {
    return ['double_joints'].includes(categoryKey);
  }

  static isIgubalCouplingJoints(categoryKey: string): boolean {
    return ['variable_double_joint'].includes(categoryKey);
  }

  static isDrygearAccessories(categoryKey: string): boolean {
    return ['drygear_accessories'].includes(categoryKey);
  }

  static isDrygearWormGearJointsAsymmetrical(categoryKey: string): boolean {
    return ['worm_gear_joints_asymmetrical'].includes(categoryKey);
  }

  static isDrygearWormGearAccessories(categoryKey: string): boolean {
    return ['acessory_worm_gears'].includes(categoryKey);
  }

  static isDylinLinearTechnology(categoryKey: string): boolean {
    return [
      'w_rails',
      'drylin_w_pillow_blocks',
      'compete_systems_drylin_w',
      'accessories_drylin_w',
      'mounting_plates_drylin_w',
      'n_rails',
      'n_guide_carriages',
      'compete_systems_drylin_n',
      'accessories_drylin_n',
      't_rails',
      't_guide_carriages',
      'compete_systems_drylin_t',
      'accessories_drylin_t',
      'r_shafts',
      'drylin_w_carriages',
      'r_pillow_blocks',
      'r_carriages',
      'r_linear_slide_bearings',
      'r_liner',
      'r_shaft_end_supports_shaft_end_blocks',
      'gliding_pads',
      'q_section_rails',
      'q_pillow_blocks',
      'q_carriages',
      'accessories_drylin_q',
      'nt_telescopic_rails',
      'complete_systems_with_measurement_technology',
    ].includes(categoryKey);
  }

  /**
   * Determines if the provided category tree contains the drylin linear technology category.
   * Assumes that the drylin linear technology category is a top level category in the PIM system.
   *
   * @param {Category} categoryTree - The category tree to be evaluated.
   * @return {boolean} Returns true if the category tree is a drylin linear technology category tree, otherwise false.
   */
  static isDylinLinearTechnologyCategoryTree(categoryTree: Category): boolean {
    if (!categoryTree) {
      return false;
    }
    return CategoryHelper.extractCategoryIdsRecursive(categoryTree).includes(CATEGORY_DRYLIN_LINEAR_TECHNOLOGY);
  }

  /**
   * Determines if the provided category tree contains the barstock category.
   *
   * @param {Category} categoryTree - The category tree to be evaluated.
   * @return {boolean} Returns true if the category tree is a barstock category tree, otherwise false.
   */
  static isBarstockCategoryTree(categoryTree: Category): boolean {
    if (!categoryTree) {
      return false;
    }
    return CategoryHelper.extractCategoryIdsRecursive(categoryTree).includes(CATEGORY_BARSTOCK);
  }

  static isTriflex(categoryKey: string): boolean {
    return [
      '3D_movement',
      '3d_energychains',
      '3d_energychains_mounting_brackets',
      '3d_energychains_retraction_systems',
      '3d_energychains_sets_retraction_systems',
      '3d_energychains_cobot',
      'scara_cable_solution',
      '3d_energychains_accessories',
      'triflex_classic',
    ].includes(categoryKey);
  }

  static isBarStock(categoryKey: string): boolean {
    return [CATEGORY_BARSTOCK].includes(categoryKey);
  }
}

/**
 * Returns if the given category is compatible for showing inch products
 */
export const isCategoryInchCompatible = (categoryKey: string, parentKey?: string) =>
  INCH_COMPATIBLE_CATEGORIES.includes(`${parentKey ? `${parentKey}.` : ''}${categoryKey}`);

/**
 * Returns if the unit system switch must be hidden in given category
 * @param categoryKey
 * @param parentKey
 */
export const isCategoryMeasurementSwitchHidden = (categoryKey: string, parentKey?: string) =>
  HIDE_UNIT_SWITCH_CATEGORIES.includes(`${parentKey ? `${parentKey}.` : ''}${categoryKey}`);
